import { get } from "@github/webauthn-json";
import { useCallback } from "react";

import {
  captureEvent,
  extractErrorProperties,
} from "context/posthog/captureEvent";
import { POSTHOG_EVENTS, POSTHOG_FLOWS } from "context/posthog/events";
// eslint-disable-next-line no-restricted-imports
import { STATUS_CODES } from "modules/common/constants/StatusCodes";

import {
  useFinalizeSCALoginMutation,
  useInitializeSCALoginMutation,
} from "../../api/ScaLoginApi";
import {
  SCA_LOGIN_ERROR_REASONS,
  SCA_LOGIN_STAGES,
  ScaLoginError,
} from "../../models/ScaLoginError";

import type {
  ScaLoginErrorReason,
  ScaLoginStage,
} from "../../models/ScaLoginError";

const ABORT_ERROR = "AbortError";
const NOT_ALLOWED_ERROR = "NotAllowedError";

export interface AuthenticateSCALoginParams {
  bearerToken: string;
  signal?: AbortSignal;
}

/**
 * Server side passkey login ceremony. The backend knows who is logging in from
 * the 2FA flow token, so the challenge is scoped to that user's passkeys and
 * the browser only offers those - unlike the client first flow, where Hanko
 * offered every passkey registered for the domain.
 *
 * Both endpoints require a 2FA flow token, so this cannot be used to step up an
 * already fully authenticated session.
 */
export function useSCALogin() {
  const [initializeSCALogin] = useInitializeSCALoginMutation();
  const [finalizeSCALogin] = useFinalizeSCALoginMutation();

  const authenticateSCALogin = useCallback(
    async ({
      bearerToken,
      signal,
    }: AuthenticateSCALoginParams): Promise<string> => {
      const initializeResult = await initializeSCALogin({ bearerToken });

      if (initializeResult.data === undefined) {
        throw createScaLoginError(
          initializeResult.error,
          SCA_LOGIN_STAGES.INITIALIZE
        );
      }

      let credential;

      try {
        credential = await get({ ...initializeResult.data, signal });
      } catch (error) {
        throw createScaLoginError(error, SCA_LOGIN_STAGES.WEBAUTHN);
      }

      const finalizeResult = await finalizeSCALogin({
        credential,
        bearerToken,
      });

      if (finalizeResult.data === undefined) {
        throw createScaLoginError(
          finalizeResult.error,
          SCA_LOGIN_STAGES.FINALIZE
        );
      }

      captureEvent(POSTHOG_EVENTS.scaAuthenticateSucceeded, {
        flow: POSTHOG_FLOWS.scaLogin,
      });

      return finalizeResult.data.token;
    },
    [finalizeSCALogin, initializeSCALogin]
  );

  return { authenticateSCALogin };
}

function createScaLoginError(error: unknown, stage: ScaLoginStage) {
  const reason = resolveErrorReason(error, stage);

  captureEvent(POSTHOG_EVENTS.scaAuthenticateFailed, {
    flow: POSTHOG_FLOWS.scaLogin,
    reason,
    stage,
    ...extractErrorProperties(error),
  });

  return new ScaLoginError(reason, stage);
}

function resolveErrorReason(
  error: unknown,
  stage: ScaLoginStage
): ScaLoginErrorReason {
  if (stage === SCA_LOGIN_STAGES.WEBAUTHN) {
    return isCancelledByUser(error)
      ? SCA_LOGIN_ERROR_REASONS.CANCELLED
      : SCA_LOGIN_ERROR_REASONS.TECHNICAL;
  }

  const status = getErrorStatus(error);

  if (status === STATUS_CODES.TOO_MANY_REQUESTS) {
    return SCA_LOGIN_ERROR_REASONS.RATE_LIMITED;
  }

  if (status === STATUS_CODES.FORBIDDEN) {
    return SCA_LOGIN_ERROR_REASONS.FORBIDDEN;
  }

  return SCA_LOGIN_ERROR_REASONS.TECHNICAL;
}

function isCancelledByUser(error: unknown) {
  return (
    error instanceof DOMException &&
    (error.name === NOT_ALLOWED_ERROR || error.name === ABORT_ERROR)
  );
}

function getErrorStatus(error: unknown) {
  if (typeof error !== "object" || error === null || !("status" in error)) {
    return undefined;
  }

  return error.status;
}
